import { useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { NetworkIcon } from "@lucide/icons";
import { useQuery } from "@tanstack/react-query";
import { parse } from "date-fns";

import { formatDateAt } from "simplydo/core/dates";
import { getSelectableDeadlineFields } from "simplydo/core/ideas";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { DateTimeInput } from "simplydo/react/ui/date";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown, type DropdownProps } from "simplydo/react/ui/dropdown";
import { Form, FormTextArea } from "simplydo/react/ui/form";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { type InputOnChangeData } from "simplydo/react/ui/input";
import { Input } from "simplydo/react/ui/input";
import { List, ListItem } from "simplydo/react/ui/list";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import { type ChallengesReducer } from "reducers/challenges";
import util from "utils/utils";

import { PaginationWithLimiter, Sticky } from "components/lib/UI";

import type {
  ChallengeDetail,
  ChallengeIdeaListItem,
  ChallengeProjectBoardDefaultLane,
  ChallengeProjectLane,
} from "../../types";
import { DeleteNameContainer, DeleteNameItem } from "./Styles";

type PaginationProps = {
  limit: number;
  page: number;
  total: number;
  updateFilters: (filters: Partial<ChallengesReducer>) => void;
  limitValues: number[];
};

const Pagination = ({
  limit,
  page,
  total,
  updateFilters,
  limitValues,
}: PaginationProps) => (
  <Sticky
    bottom={15}
    style={{
      textAlign: "center",
      zIndex: 10,
      display: "flex",
      justifyContent: "center",
      marginTop: 10,
    }}
  >
    <PaginationWithLimiter
      limit={limit}
      page={page}
      total={total}
      limitValues={limitValues}
      updateLimit={(value) => updateFilters({ ideaLimit: value })}
      updatePage={(value) => updateFilters({ ideaPage: value })}
    />
  </Sticky>
);

type IdeaDeletionModalProps = {
  challengeId: string;
  ideasToDelete?: ChallengeIdeaListItem[];
  setIdeasToDelete: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  setSelectedIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
};

const IdeaDeletionModal = ({
  challengeId,
  ideasToDelete = [],
  setIdeasToDelete,
  setSelectedIdeas,
}: IdeaDeletionModalProps) => {
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const { t } = useTranslation();

  // Get all child ideas from all ideas to delete by flattening
  const allChildIdeas = useMemo(() => {
    const allChildren: ChallengeIdeaListItem[] = [];
    ideasToDelete.forEach((idea) => {
      if (idea.ownerChildren) {
        allChildren.push(...idea.ownerChildren);
      }
    });
    return allChildren;
  }, [ideasToDelete]);

  return (
    <Modal open={!!ideasToDelete.length} onClose={() => setIdeasToDelete([])}>
      <ModalHeader>Delete {t("generic.ideas")}</ModalHeader>
      <ModalContent scrolling>
        <p>You are choosing to delete the following {t("generic.ideas")}:</p>
        <List variant="bulleted" compact>
          {ideasToDelete.map((idea) => (
            <ListItem key={idea._id}>
              {idea.children ? <Icon icon={NetworkIcon} /> : null}
              <b>
                {idea.name}
                {idea.owner ? ` - ${idea.owner.profile.fullName}` : ""}
              </b>
            </ListItem>
          ))}
        </List>
        {allChildIdeas.length ? (
          <>
            <p>
              The following child {t("generic.ideas")} will be removed from the
              deleted merged {t("generic.ideas")}, and returned to the{" "}
              {t("generic.challenge")} as individual {t("generic.ideas")}.
            </p>
            <List variant="bulleted" compact>
              {allChildIdeas.map((idea) => (
                <ListItem key={idea._id}>
                  <b>{idea.name}</b>
                </ListItem>
              ))}
            </List>
            <p>
              Any comments left on the merged {t("generic.ideas")} will be
              deleted, but any comments left on the original{" "}
              {t("generic.ideas")} will be returned to them.
            </p>
          </>
        ) : null}
        <Message color="red">
          This action is irreversible. These {t("generic.ideas")} will be
          permanently deleted, and cannot be recovered. Please make sure this
          action is correct, and double check the {t("generic.ideas")} you have
          chosen.
        </Message>
        <DeleteNameContainer>
          <DeleteNameItem>
            <p>
              Type the word &apos;DELETE&apos; below to confirm you understand
              this.
            </p>
            <Input
              placeholder="DELETE"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
            />
          </DeleteNameItem>
        </DeleteNameContainer>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setIdeasToDelete([])}>Cancel</Button>
        <Button
          disabled={deleteConfirm !== "DELETE"}
          color="red"
          onClick={() => {
            util
              .confirm(
                `Delete ${t("generic.ideas")}`,
                `Are you absolutely sure you want to permanently delete these ${t("generic.ideas")}?`,
              )
              .then(() => {
                api.ideas.bulkDelete(
                  ideasToDelete.map((i) => i._id),
                  () => {
                    api.setQueryData(
                      api.challenges.getIdeas,
                      [challengeId],
                      (prev) => ({
                        ...prev,
                        ideas: prev.ideas.filter(
                          (i) =>
                            ideasToDelete.map((i) => i._id).indexOf(i._id) ===
                            -1,
                        ),
                      }),
                    );
                    // Invalidate challenge query to update idea count
                    api.invalidateQueries(api.challenges.get, [challengeId]);
                    api.invalidateQueries(api.challenges.getIdeas, [
                      challengeId,
                    ]);
                    setSelectedIdeas((prev) =>
                      prev.filter(
                        (p) =>
                          ideasToDelete.map((i) => i._id).indexOf(p._id) === -1,
                      ),
                    );
                    setIdeasToDelete([]);
                    toast.success(
                      `${t("common:capitalise", {
                        key: "generic.ideas",
                      })} deleted`,
                    );
                  },
                  (err) => toast.error(err.message),
                );
                setDeleteConfirm("");
              })
              .catch(() => {
                setDeleteConfirm("");
              });
          }}
        >
          Delete
        </Button>
      </ModalActions>
    </Modal>
  );
};

type IdeaDeadlineModalProps = {
  challengeId: string;
  ideaTemplate?: OpenAPI.Schemas["IdeaTemplate"];
  deadlineIdeas?: ChallengeIdeaListItem[];
  setDeadlineIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
};

const IdeaDeadlineModal = ({
  challengeId,
  ideaTemplate,
  deadlineIdeas = [],
  setDeadlineIdeas,
}: IdeaDeadlineModalProps) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [editableFields, setEditableFields] = useState<string[]>([]);
  const [shouldUnsubmit, setShouldUnsubmit] = useState(true);
  const [notifyAuthors, setNotifyAuthors] = useState(true);
  const getDefaultDeadline = () =>
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const [deadline, setDeadline] = useState(getDefaultDeadline);
  const [deadlineLoading, setDeadlineLoading] = useState(false);

  const resetModalState = () => {
    setDeadlineIdeas([]);
    setEditableFields([]);
    setNote("");
    setShouldUnsubmit(true);
    setNotifyAuthors(true);
    setDeadline(getDefaultDeadline());
  };

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId]),
  );

  const selectableFields = useMemo(
    () => getSelectableDeadlineFields(ideaTemplate, t),
    [ideaTemplate, t],
  );

  const fieldOptions = useMemo(
    () =>
      selectableFields.map((field) => ({
        key: field.id,
        value: field.id,
        text: field.sectionName
          ? `${field.sectionName}: ${field.title}`
          : field.title,
      })),
    [selectableFields],
  );

  return (
    <Modal open={!!deadlineIdeas.length} onClose={resetModalState}>
      <ModalHeader>Set {t("generic.idea")} deadline</ModalHeader>
      <ModalContent scrolling>
        <p>
          You are setting a deadline for the following {t("generic.ideas")}:
        </p>
        <List variant="bulleted" compact>
          {deadlineIdeas.map((idea) => (
            <ListItem key={idea._id}>
              <b>{idea.name}</b>
              {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
            </ListItem>
          ))}
        </List>
        <Divider />
        <b>Deadline</b>
        <p>
          The{" "}
          {util.pluralise(
            deadlineIdeas.length,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}{" "}
          will be submissible until the chosen date, irrespective of the
          deadline chosen for this {t("generic.challenge")}.
        </p>
        {challengeQuery.data?.deadline ? (
          <p>
            The deadline for this {t("generic.challenge")} is{" "}
            {formatDateAt(challengeQuery.data.deadline)}.
          </p>
        ) : null}
        <DateTimeInput
          value={deadline}
          dateTimeFormat="yyyy-MM-dd HH:mm"
          onChange={(_e: React.SyntheticEvent, { value }: InputOnChangeData) =>
            setDeadline(parse(value, "yyyy-MM-dd HH:mm", new Date()))
          }
        />
        <Divider hidden />
        <b>Notes</b>
        <p>
          The context of these notes will be sent to the {t("generic.idea")}{" "}
          authors once you have set the new deadline. You can choose to provide
          details for the authors regarding their submission or any other
          relevant information.
        </p>
        <Form>
          <FormTextArea
            placeholder="Add a note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Form>
        {fieldOptions.length > 0 ? (
          <>
            <Divider hidden />
            <b>{t("ideas.deadline.modal.editableFields.title")}</b>
            <p>{t("ideas.deadline.modal.editableFields.description")}</p>
            <Dropdown
              fluid
              multiple
              variant="outline"
              search
              clearable
              placeholder={t("ideas.deadline.modal.editableFields.placeholder")}
              options={fieldOptions}
              value={editableFields}
              onChange={(_e, { value }) =>
                setEditableFields(
                  Array.isArray(value)
                    ? value.filter((v): v is string => typeof v === "string")
                    : [],
                )
              }
            />
          </>
        ) : null}
        <Divider hidden />
        <Checkbox
          toggle
          checked={shouldUnsubmit}
          onChange={(_e, d) => setShouldUnsubmit(!!d.checked)}
        >
          <CheckboxHeader>
            <H5>{`Unsubmit ${t("generic.ideas")}`}</H5>
          </CheckboxHeader>
          <CheckboxContent>{`If ${t("generic.ideas")} are submitted they will return to their draft format.`}</CheckboxContent>
        </Checkbox>
        <Checkbox
          toggle
          checked={notifyAuthors}
          onChange={(_e, d) => setNotifyAuthors(!!d.checked)}
        >
          <CheckboxHeader>
            <H5>Notify authors</H5>
          </CheckboxHeader>
          <CheckboxContent>
            Authors will be notified of the new deadline and any notes you have
            added. This will also include the changed submission status (if
            applicable).
          </CheckboxContent>
        </Checkbox>
      </ModalContent>
      <ModalActions>
        <Button onClick={resetModalState}>Cancel</Button>
        <Button
          onClick={() => {
            util
              .confirm(
                "Remove deadline",
                "Are you sure you want to remove the deadline from these ideas?",
              )
              .then(() => {
                api.ideas.removeDeadline(
                  deadlineIdeas.map((i) => i._id),
                  ({ ideas }) => {
                    toast.success("Deadlines removed");
                    resetModalState();
                    api.setQueryData(
                      api.challenges.getIdeas,
                      [challengeId],
                      (prev) => ({
                        ...prev,
                        ideas: prev.ideas.map(
                          (i) => ideas.find((u) => u._id === i._id) ?? i,
                        ),
                      }),
                    );
                    for (const idea of deadlineIdeas) {
                      api.invalidateQueries(api.ideas.get, [idea._id]);
                    }
                  },
                  (err) => toast.error(err.message),
                );
              })
              .catch(() => {});
          }}
          variant="outline"
        >
          Remove deadline
        </Button>
        <Button
          loading={deadlineLoading}
          disabled={deadlineLoading}
          onClick={() => {
            const deadlineIdeasIds = deadlineIdeas.map((i) => i._id);
            setDeadlineLoading(true);
            api.ideas.setDeadline(
              {
                ideaIds: deadlineIdeasIds,
                deadlineData: {
                  deadline: new Date(deadline).toISOString(),
                  note,
                  editableFields,
                },
                shouldUnsubmit,
                notifyAuthors,
              },
              () => {
                setDeadlineLoading(false);
                resetModalState();
                toast.success("Deadline set");
                api.setQueryData(
                  api.challenges.getIdeas,
                  [challengeId],
                  (prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      ideas: prev.ideas.map((i) => {
                        if (deadlineIdeasIds.indexOf(i._id) === -1) return i;
                        return {
                          ...i,
                          submissionDeadline: {
                            deadline: new Date(deadline).toISOString(),
                            note,
                            editableFields,
                          },
                          isSubmitted: shouldUnsubmit ? false : i.isSubmitted,
                          submittedBy: shouldUnsubmit
                            ? undefined
                            : i.submittedBy,
                          submittedAt: shouldUnsubmit
                            ? undefined
                            : i.submittedAt,
                        };
                      }),
                    };
                  },
                );
                for (const idea of deadlineIdeas) {
                  api.invalidateQueries(api.ideas.get, [idea._id]);
                }
              },
              (err) => {
                setDeadlineLoading(false);
                toast.error(err.message);
              },
            );
          }}
          color="primary"
        >
          Set deadline
        </Button>
      </ModalActions>
    </Modal>
  );
};

type IdeaManagementDeadlineModalProps = {
  managementDeadlineIdeas?: ChallengeIdeaListItem[];
  setManagementDeadlineIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  updateIdeaProjectDeadlineMulti: (deadline: Date) => void;
};

const IdeaManagementDeadlineModal = ({
  managementDeadlineIdeas = [],
  setManagementDeadlineIdeas,
  updateIdeaProjectDeadlineMulti,
}: IdeaManagementDeadlineModalProps) => {
  const { t } = useTranslation();
  const now = new Date();
  const defaultDeadline = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const [managementDeadline, setManagementDeadline] = useState(defaultDeadline);
  return (
    <Modal
      open={!!managementDeadlineIdeas.length}
      onClose={() => setManagementDeadlineIdeas([])}
    >
      <ModalHeader>Set {t("generic.idea")} management deadline</ModalHeader>
      <ModalContent scrolling>
        {" "}
        <div style={{ marginBottom: "25px" }}>
          <p>
            You are setting a management deadline for the following{" "}
            {t("generic.ideas")}:
          </p>
          <List variant="bulleted" compact>
            {managementDeadlineIdeas.map((idea) => (
              <ListItem key={idea._id}>
                <b>{idea.name}</b>
                {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
              </ListItem>
            ))}
          </List>
        </div>
        <Divider />
        <div>
          <b>Deadline</b>
          <p>Set a project deadline for all selected {t("generic.ideas")}</p>
          <DateTimeInput
            value={managementDeadline}
            dateTimeFormat="yyyy-MM-dd HH:mm"
            onChange={(
              _e: React.SyntheticEvent,
              { value }: InputOnChangeData,
            ) =>
              setManagementDeadline(
                parse(value, "yyyy-MM-dd HH:mm", new Date()),
              )
            }
            minDate={now}
          />
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setManagementDeadlineIdeas([])}>Cancel</Button>
        <Button
          onClick={() => {
            updateIdeaProjectDeadlineMulti(managementDeadline);
            setManagementDeadlineIdeas([]);
          }}
          color="primary"
        >
          Set deadline
        </Button>
      </ModalActions>
    </Modal>
  );
};

type IdeaMoveToLaneModalProps = {
  moveLaneIdeas?: ChallengeIdeaListItem[];
  setMoveLaneIdeas: Dispatch<SetStateAction<ChallengeIdeaListItem[]>>;
  allChallengeProjectLanes?: ChallengeProjectLane[];
  checkUpdateProjectLane: (
    ideaIds: string[],
    laneId: string,
    order?: number | null,
  ) => void;
  challenge: ChallengeDetail;
};

const IdeaMoveToLaneModal = ({
  moveLaneIdeas = [],
  setMoveLaneIdeas,
  allChallengeProjectLanes = [],
  checkUpdateProjectLane,
  challenge,
}: IdeaMoveToLaneModalProps) => {
  const { t } = useTranslation();
  const defaultLane = challenge.projectBoard?.defaultLane as
    | ChallengeProjectBoardDefaultLane
    | undefined;
  const [selectedLaneId, setSelectedLaneId] = useState(
    defaultLane?._id ?? "default",
  );

  return (
    <Modal open={!!moveLaneIdeas.length} onClose={() => setMoveLaneIdeas([])}>
      <ModalHeader>
        Move{" "}
        {util.pluralise(
          moveLaneIdeas.length,
          t("generic.idea"),
          t("generic.ideas"),
          false,
        )}{" "}
        to project lane
      </ModalHeader>

      <ModalContent scrolling>
        <p>
          You are moving the following{" "}
          {util.pluralise(
            moveLaneIdeas.length,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}{" "}
          to a new project lane:
        </p>
        <List variant="bulleted" compact>
          {moveLaneIdeas.map((idea) => (
            <ListItem key={idea._id}>
              <b>{idea.name}</b>
              {idea.owner ? ` by ${idea.owner.profile.fullName}` : ""}
            </ListItem>
          ))}
        </List>

        <Divider />

        <b>Select lane</b>
        <p>
          Select the lane where these{" "}
          {util.pluralise(
            moveLaneIdeas.length,
            t("generic.idea"),
            t("generic.ideas"),
            false,
          )}{" "}
          will be moved.
        </p>
        <Dropdown
          variant="outline"
          fluid
          value={selectedLaneId}
          options={[
            {
              key: "default",
              value: "default",
              text: defaultLane?.name ?? "Default",
            },
            ...allChallengeProjectLanes.map((lane) => ({
              key: lane._id,
              value: lane._id,
              text: lane.name,
            })),
          ]}
          onChange={(_e, { value }) => setSelectedLaneId(String(value))}
        />
      </ModalContent>

      <ModalActions>
        <Button onClick={() => setMoveLaneIdeas([])}>Cancel</Button>
        <Button
          onClick={() => {
            checkUpdateProjectLane(
              moveLaneIdeas.map((idea) => idea._id!),
              selectedLaneId,
            );
            setMoveLaneIdeas([]);
          }}
          color="primary"
        >
          Move ideas
        </Button>
      </ModalActions>
    </Modal>
  );
};

export {
  IdeaDeadlineModal,
  IdeaDeletionModal,
  IdeaManagementDeadlineModal,
  IdeaMoveToLaneModal,
  Pagination,
};
